/**
 * Gifts Progress Web Component
 * 
 * A comprehensive Shopify cart enhancement component that provides:
 * 
 * 🎯 **Core Features:**
 * - Dynamic progress bar showing cart value progression
 * - Multiple milestone types (free shipping, free gifts)
 * - Automatic gift addition/removal based on cart thresholds
 * - Real-time cart synchronization and state management
 * 
 * 🛒 **Cart Integration:**
 * - Intercepts and manages Shopify cart events
 * - Prevents theme cart drawer interference
 * - Debounced cart updates to reduce API calls
 * - Fresh cart data validation for accurate state
 * 
 * 🎁 **Gift Management:**
 * - Auto-adds eligible gifts when thresholds are met
 * - Removes ineligible gifts when cart value drops
 * - Tracks gift quantities (1 free + unlimited paid)
 * - Cleans up orphaned gifts from cart
 * 
 * 🔧 **Technical Features:**
 * - Event-driven architecture with custom event handling
 * - Debounced cart operations to prevent race conditions
 * - Comprehensive error handling and logging
 * - Debug mode for development (set debug = true)
 * - Initialization guards to prevent multiple setups
 * 
 * 📊 **Progress Visualization:**
 * - Visual progress bar with milestone markers
 * - Dynamic messaging based on cart status
 * - Responsive milestone positioning
 * - Achievement state management
 * 
 * 🚀 **Usage:**
 * ```html
 * <gifts-progress>
 *   <!-- Liquid-rendered milestone blocks -->
 * </gifts-progress>
 * ```
 * 
 * 🐛 **Debug Mode:**
 * ```javascript
 * document.querySelector('gifts-progress').debug = true;
 * ```
 * 
 * @version 2.0.0
 * @author Enchant Digital
 * @requires Shopify Liquid templates for milestone configuration
 */

// Prevent redeclaration if script is loaded multiple times
if (!window.customElements.get('gifts-progress')) {
  
class GiftsProgress extends HTMLElement {
  constructor() {
    super();
    this.milestones = [];
    this.cartTotal = 0;
    this.addedGifts = new Map(); // Changed to Map to track gift details
    this.giftLineItems = new Map(); // Track line item IDs for gifts
    this.abortController = new AbortController();
    this.isProcessingCartChange = false; // Flag to prevent recursive cart events
    this.pendingCartUpdates = new Set(); // Track pending cart updates
    this.cartEventDebounceTimer = null; // Debounce timer for cart events
    this.lastCartTotal = 0; // Track last cart total to avoid unnecessary updates
    this.isInitialized = false; // Prevent multiple initializations
    this.initializationPromise = null; // Track initialization promise
    this.lastInitializationTime = 0; // Track last initialization time
    this.debug = false; // Debug flag - set to true to enable console logging
  }

  // Helper method for debug logging
  debugLog(...args) {
    if (this.debug) {
      console.log(...args);
    }
  }

  debugWarn(...args) {
    if (this.debug) {
      console.warn(...args);
    }
  }

  debugError(...args) {
    if (this.debug) {
      console.error(...args);
    }
  }

  async connectedCallback() {
    // Prevent multiple initializations
    if (this.isInitialized) {
      this.debugLog('[GIFTS-PROGRESS] Already initialized, skipping...');
      return;
    }
    
    // Debounce rapid re-initializations
    const now = Date.now();
    if (now - this.lastInitializationTime < 1000) {
      this.debugLog('[GIFTS-PROGRESS] Too soon since last initialization, skipping...');
      return;
    }
    this.lastInitializationTime = now;
    
    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      this.debugLog('[GIFTS-PROGRESS] Initialization in progress, waiting...');
      await this.initializationPromise;
      return;
    }
    
    // Start initialization
    this.initializationPromise = this.initialize();
    await this.initializationPromise;
  }

  async initialize() {
    try {
      this.debugLog('[GIFTS-PROGRESS] Initializing...');
      
      // Disable theme cart events to prevent interference
      this.disableThemeCartEvents();
      
      // Get milestones from DOM (rendered by Liquid)
      this.scanMilestones();
      
      // Get initial cart total
      this.cartTotal = window.Shopify?.cart?.total_price || 0;
      
      this.setupEventListeners();
      await this.syncWithCart();
      this.validateGiftProducts();
      this.updateProgress();
      await this.checkForAutoGifts();
      
      // Force cleanup of any ineligible gifts that might still be in cart
      await this.cleanupIneligibleGifts();
      
      // Mark as initialized to show progress bar and hide fallback
      this.classList.add('gifts-progress--initialized');
      this.isInitialized = true;
      
      this.debugLog('[GIFTS-PROGRESS] Initialized with', this.milestones.length, 'milestones');
    } catch (error) {
      this.debugError('[GIFTS-PROGRESS] Failed to initialize', error);
    } finally {
      this.initializationPromise = null;
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    
    // Re-enable theme cart events
    this.enableThemeCartEvents();
    
    // Clear any pending debounce timers
    if (this.cartEventDebounceTimer) {
      clearTimeout(this.cartEventDebounceTimer);
      this.cartEventDebounceTimer = null;
    }
  }

  disableThemeCartEvents() {
    this.debugLog('[GIFTS-PROGRESS] Setting up cart event interception to prevent interference...');
    
    // Add a high-priority event listener that stops propagation of cart events
    // when gifts progress is processing
    this.cartEventInterceptor = (event) => {
      if (this.isProcessingCartChange) {
        this.debugLog('[GIFTS-PROGRESS] Intercepted and stopped cart event:', event.type);
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    };
    
    // Add the interceptor with high priority (capture phase)
    document.addEventListener('cart:change', this.cartEventInterceptor, { capture: true });
    document.addEventListener('cart:refresh', this.cartEventInterceptor, { capture: true });
    document.addEventListener('variant:add', this.cartEventInterceptor, { capture: true });
    document.addEventListener('line-item:change', this.cartEventInterceptor, { capture: true });
  }

  enableThemeCartEvents() {
    this.debugLog('[GIFTS-PROGRESS] Removing cart event interception...');
    
    // Remove the interceptor
    if (this.cartEventInterceptor) {
      document.removeEventListener('cart:change', this.cartEventInterceptor, { capture: true });
      document.removeEventListener('cart:refresh', this.cartEventInterceptor, { capture: true });
      document.removeEventListener('variant:add', this.cartEventInterceptor, { capture: true });
      document.removeEventListener('line-item:change', this.cartEventInterceptor, { capture: true });
      this.cartEventInterceptor = null;
    }
  }

  async forceCartDrawerRefresh() {
    try {
      this.debugLog('[GIFTS-PROGRESS] Forcing cart drawer refresh...');
      
      // Temporarily disable our event interceptor
      const wasProcessing = this.isProcessingCartChange;
      this.isProcessingCartChange = false;
      
      // Get fresh cart data
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      // Update our internal cart total to prevent re-processing
      this.cartTotal = cart.total_price;
      this.lastCartTotal = cart.total_price;
      
      // Mark that we're doing a manual refresh to prevent re-processing
      this.isManualRefresh = true;
      
      // Dispatch cart events to update the UI
      document.dispatchEvent(new CustomEvent('cart:change', {
        detail: { 
          cart: cart,
          baseEvent: 'gifts-progress:refresh'
        }
      }));
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
      
      // Restore processing state
      this.isProcessingCartChange = wasProcessing;
      
      // Clear manual refresh flag after a short delay
      setTimeout(() => {
        this.isManualRefresh = false;
      }, 100);
      
      this.debugLog('[GIFTS-PROGRESS] Cart drawer refresh completed');
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to force cart drawer refresh:', error);
    }
  }

  scanMilestones() {
    this.milestones = [];
    
    // Scan individual blocks from content_for 'blocks'
    const shippingBlocks = this.querySelectorAll('.free-shipping-block');
    const giftBlocks = this.querySelectorAll('.free-gift-block');
    
    [...shippingBlocks, ...giftBlocks].forEach(block => {
      const milestone = {
        element: block,
        threshold: parseInt(block.dataset.threshold) || 0,
        type: block.classList.contains('free-shipping-block') ? 'shipping' : 'gift',
        variantId: block.dataset.variantId,
        // All gift milestones should auto-add by default
        autoAdd: block.classList.contains('free-gift-block'),
        // Track if the gift should be hidden from the progress bar
        hidden: block.dataset.hidden === 'true',
        // Track if the gift requires subscription
        triggerMode: block.dataset.triggerMode || 'any'
      };
      
      if (milestone.threshold > 0) {
        // For gift milestones, validate that we have a variant ID
        if (milestone.type === 'gift' && (!milestone.variantId || milestone.variantId === '')) {
          this.debugWarn('[GIFTS-PROGRESS] Gift milestone missing variant ID:', milestone);
          return;
        }
        
        this.milestones.push(milestone);
        this.debugLog('[GIFTS-PROGRESS] Found milestone:', milestone);
      }
    });
    
    // Sort by threshold
    this.milestones.sort((a, b) => a.threshold - b.threshold);
    
    this.debugLog('[GIFTS-PROGRESS] Scanned milestones:', this.milestones);
  }

  setupEventListeners() {
    this.debugLog('[GIFTS-PROGRESS] Setting up event listeners...');
    
    // Single consolidated cart change handler to reduce redundancy
    const handleCartChange = (event) => {
      // Skip if not initialized yet
      if (!this.isInitialized) {
        this.debugLog('[GIFTS-PROGRESS] Skipping cart change (not initialized yet)');
        return;
      }
      
      // Skip if this is a manual refresh we triggered
      if (this.isManualRefresh) {
        this.debugLog('[GIFTS-PROGRESS] Skipping cart change (manual refresh in progress)');
        return;
      }
      
      if (this.isProcessingCartChange) {
        this.debugLog('[GIFTS-PROGRESS] Ignoring cart change event (already processing)');
        return;
      }
      
      // Skip if cart total hasn't actually changed
      const newTotal = event.detail.cart.total_price;
      if (newTotal === this.cartTotal) {
        this.debugLog('[GIFTS-PROGRESS] Skipping update (cart total unchanged)');
        return;
      }
      
      this.debugLog('[GIFTS-PROGRESS] Cart change event received', event.detail);
      this.cartTotal = newTotal;
      this.updateProgress();
      this.checkForAutoGifts(event.detail.cart);
    };
    
    // Listen to cart changes - prevent recursive processing
    document.addEventListener('cart:change', handleCartChange, { signal: this.abortController.signal });
    document.addEventListener('variant:add', handleCartChange, { signal: this.abortController.signal });
    document.addEventListener('line-item:change', handleCartChange, { signal: this.abortController.signal });
  }

  async updateFromCart() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await response.json();
      this.cartTotal = cart.total_price;
      this.updateProgress();
      this.checkForAutoGifts(cart);
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to fetch cart for gifts progress:', error);
    }
  }

  hasSubscriptionInCart(cartItems) {
    for (const item of cartItems) {
      // Check for Shopify native subscriptions
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        this.debugLog('[GIFTS-PROGRESS] Found Shopify subscription:', {
          product: item.product_title,
          plan: item.selling_plan_allocation.selling_plan
        });
        return true;
      }
      
      // Check for custom subscription implementations
      if (item.properties && 
          (item.properties.subscription === 'true' || 
           item.properties.subscription_id || 
           item.properties.frequency_num || 
           item.properties.frequency_type || 
           item.properties.frequency_type_text)) {
        this.debugLog('[GIFTS-PROGRESS] Found custom subscription:', {
          product: item.product_title,
          properties: item.properties
        });
        return true;
      }
    }
    
    this.debugLog('[GIFTS-PROGRESS] No subscription found in cart');
    return false;
  }

  async checkForAutoGifts(cart = null) {
    if (!cart) {
      this.fetchCartAndCheckGifts();
      return;
    }

    // Prevent recursive processing
    if (this.isProcessingCartChange) {
      this.debugLog('[GIFTS-PROGRESS] Already processing cart change, skipping checkForAutoGifts');
      return;
    }

    this.isProcessingCartChange = true;
    this.pendingCartUpdates.clear();

    try {
      const cartItems = cart.items || [];
      const hasSubscription = this.hasSubscriptionInCart(cartItems);
      
      this.debugLog('[GIFTS-PROGRESS] Processing cart with', cartItems.length, 'items, total:', this.cartTotal, 'hasSubscription:', hasSubscription);

      // Check each gift milestone
      for (const milestone of this.milestones) {
        if (milestone.type === 'gift' && milestone.autoAdd && milestone.variantId) {
          // Check if the gift is eligible based on cart total and subscription mode
          const meetsThreshold = this.cartTotal >= milestone.threshold;
          const meetsSubscriptionRequirement = milestone.triggerMode === 'any' || 
            (milestone.triggerMode === 'subscription' && hasSubscription);
          
          const isEligible = meetsThreshold && meetsSubscriptionRequirement;
          const giftKey = `${milestone.variantId}_${milestone.threshold}`;
          
          // Find existing FREE gift items for this milestone (with gift properties)
          const existingFreeGiftItems = cartItems.filter(item => 
            item.variant_id === parseInt(milestone.variantId) && 
            item.properties && 
            item.properties._gift === 'true' &&
            item.properties._gift_key === giftKey
          );
          
          // Find existing PAID gift items (same variant but without gift properties)
          const existingPaidGiftItems = cartItems.filter(item => 
            item.variant_id === parseInt(milestone.variantId) && 
            (!item.properties || item.properties._gift !== 'true')
          );
          
          const hasFreeGift = existingFreeGiftItems.length > 0;
          const totalFreeGiftQuantity = existingFreeGiftItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalPaidGiftQuantity = existingPaidGiftItems.reduce((sum, item) => sum + item.quantity, 0);

          this.debugLog(`[GIFTS-PROGRESS] Checking gift milestone: ${giftKey}, eligible: ${isEligible}, hasFreeGift: ${hasFreeGift}, freeQty: ${totalFreeGiftQuantity}, paidQty: ${totalPaidGiftQuantity}, tracked: ${this.addedGifts.has(giftKey)}`);

          if (isEligible && !hasFreeGift && !this.addedGifts.has(giftKey)) {
            // Add free gift when threshold is met and no free gift exists
            this.debugLog('[GIFTS-PROGRESS] Adding free gift:', giftKey);
            this.pendingCartUpdates.add('add');
            await this.addGiftToCart(milestone, giftKey);
          } else if (isEligible && hasFreeGift && totalFreeGiftQuantity > 1) {
            // If user manually increased quantity of free gift, reduce it back to 1
            // This ensures only 1 free gift, but allows paid quantities
            this.debugLog('[GIFTS-PROGRESS] Reducing free gift quantity to 1:', giftKey);
            this.pendingCartUpdates.add('adjust');
            await this.adjustGiftQuantity(milestone, giftKey, cartItems, 1);
          } else if (!isEligible && hasFreeGift && this.addedGifts.has(giftKey)) {
            // Remove free gift when threshold is no longer met
            // But keep any paid quantities of the same product
            this.debugLog('[GIFTS-PROGRESS] Removing free gift:', giftKey);
            this.pendingCartUpdates.add('remove');
            await this.removeGiftFromCart(milestone, giftKey, cartItems);
          }
        }
      }
    } finally {
      this.isProcessingCartChange = false;
      
      // Only dispatch a single cart change event if we made any updates
      // and batch all updates together
      if (this.pendingCartUpdates.size > 0) {
        try {
          // Small delay to allow any other pending updates to complete
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          
          // Determine the most appropriate base event
          const baseEvent = this.pendingCartUpdates.has('add') ? 'variant:add' : 
                           this.pendingCartUpdates.has('remove') ? 'line-item:change' : 
                           'line-item:change';
          
          this.dispatchCartChangeEvent(cart, baseEvent);
        } catch (error) {
          this.debugWarn('[GIFTS-PROGRESS] Failed to dispatch final cart change event:', error);
        }
      }
    }
  }

  async fetchCartAndCheckGifts() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await response.json();
      this.cartTotal = cart.total_price;
      this.updateProgress();
      this.checkForAutoGifts(cart);
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to fetch cart for gifts progress:', error);
    }
  }

  async syncWithCart() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await response.json();
      this.cartTotal = cart.total_price;
      
      // Clear existing tracking
      this.addedGifts.clear();
      this.giftLineItems.clear();
      
      // Sync with existing cart items
      cart.items.forEach(item => {
        if (item.properties && item.properties._gift === 'true' && item.properties._gift_key) {
          const giftKey = item.properties._gift_key;
          const threshold = parseInt(item.properties._gift_threshold);
          
          // Only track gifts that are still eligible (above threshold)
          const isEligible = this.cartTotal >= threshold;
          
          if (isEligible) {
            this.addedGifts.set(giftKey, {
              variantId: item.variant_id.toString(),
              threshold: threshold,
              lineItemKey: item.key
            });
            this.giftLineItems.set(giftKey, item.key);
            this.debugLog('[GIFTS-PROGRESS] Tracking eligible gift:', giftKey);
          } else {
            this.debugLog('[GIFTS-PROGRESS] Gift no longer eligible, will be removed:', giftKey);
          }
        }
      });
      
      // Clean up any orphaned gifts (gifts in cart but not in our milestones)
      await this.cleanupOrphanedGifts(cart);
      
      this.debugLog('[GIFTS-PROGRESS] Synced with cart:', this.addedGifts.size, 'eligible gifts found');
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to sync with cart:', error);
    }
  }

  async cleanupIneligibleGifts() {
    try {
      this.debugLog('[GIFTS-PROGRESS] Cleaning up ineligible gifts...');
      
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cart = await response.json();
      
      // Find all gift items that are no longer eligible
      const ineligibleGifts = cart.items.filter(item => {
        if (!item.properties || item.properties._gift !== 'true') {
          return false;
        }
        
        const threshold = parseInt(item.properties._gift_threshold);
        return this.cartTotal < threshold;
      });
      
      if (ineligibleGifts.length > 0) {
        this.debugLog('[GIFTS-PROGRESS] Found', ineligibleGifts.length, 'ineligible gifts to remove');
        
        for (const gift of ineligibleGifts) {
          const lineNumber = cart.items.indexOf(gift) + 1;
          this.debugLog('[GIFTS-PROGRESS] Removing ineligible gift at line', lineNumber);
          
          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0
            })
          });
          
          if (response.ok) {
            this.debugLog('[GIFTS-PROGRESS] ✅ Removed ineligible gift');
          } else {
            this.debugWarn('[GIFTS-PROGRESS] Failed to remove ineligible gift:', await response.text());
          }
        }
        
        // Force cart refresh after cleanup
        this.forceCartDrawerRefresh();
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to cleanup ineligible gifts:', error);
    }
  }

  async cleanupOrphanedGifts(cart) {
    const giftMilestoneVariantIds = new Set(
      this.milestones
        .filter(m => m.type === 'gift' && m.variantId)
        .map(m => parseInt(m.variantId))
    );

    const orphanedGifts = cart.items.filter(item => 
      item.properties && 
      item.properties._gift === 'true' && 
      !giftMilestoneVariantIds.has(item.variant_id)
    );

    for (const orphanedGift of orphanedGifts) {
      this.debugLog('[GIFTS-PROGRESS] Removing orphaned gift:', orphanedGift.product_id);
      try {
        // Use the line number (1-based index) for cart change API
        const lineNumber = cart.items.indexOf(orphanedGift) + 1;
        
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0
          })
        });
        
        if (response.ok) {
          this.debugLog('[GIFTS-PROGRESS] Orphaned gift removed successfully');
        }
      } catch (error) {
        this.debugWarn('[GIFTS-PROGRESS] Failed to remove orphaned gift:', error);
      }
    }
  }

  async validateGiftProducts() {
    const giftMilestones = this.milestones.filter(m => m.type === 'gift' && m.variantId);
    
    for (const milestone of giftMilestones) {
      try {
        // For variant validation, we can try to add it to cart with quantity 0 to check if it exists
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(milestone.variantId),
            quantity: 0,
            properties: { '_validation': 'true' }
          })
        });
        
        if (response.ok) {
          this.debugLog(`[GIFTS-PROGRESS] ✅ Gift variant validated: ${milestone.variantId}`);
        } else {
          if (response.status === 422) {
            this.debugError(`[GIFTS-PROGRESS] ❌ Gift variant not available: ${milestone.variantId}. Please check if the variant exists and is in stock.`);
          } else {
            this.debugError(`[GIFTS-PROGRESS] ❌ Failed to validate gift variant ${milestone.variantId}: ${response.status}`);
          }
        }
      } catch (error) {
        this.debugError(`[GIFTS-PROGRESS] ❌ Error validating gift variant ${milestone.variantId}:`, error);
      }
    }
  }

  async addGiftToCart(milestone, giftKey) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Adding gift to cart:', giftKey);
      
      // Validate variant ID
      if (!milestone.variantId || milestone.variantId === '') {
        this.debugWarn('[GIFTS-PROGRESS] No variant ID provided for gift milestone:', giftKey);
        return;
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(milestone.variantId),
          quantity: 1,
          properties: {
            '_gift': 'true',
            '_gift_threshold': milestone.threshold,
            '_gift_key': giftKey
          }
        })
      });

      if (response.ok) {
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        
        // Find the gift line item in the updated cart
        const giftLineItem = cart.items.find(item => 
          item.variant_id === parseInt(milestone.variantId) && 
          item.properties && 
          item.properties._gift === 'true' &&
          item.properties._gift_key === giftKey
        );
        
        if (giftLineItem) {
          // Store the line item ID for future removal
          this.giftLineItems.set(giftKey, giftLineItem.key);
          this.addedGifts.set(giftKey, {
            variantId: milestone.variantId,
            threshold: milestone.threshold,
            lineItemKey: giftLineItem.key
          });
        }
        
        // Don't dispatch individual cart events - let the batch handler do it
        
        this.debugLog('[GIFTS-PROGRESS] Gift added to cart:', milestone.variantId);
      } else {
        this.debugWarn('[GIFTS-PROGRESS] Failed to add gift to cart:', await response.text());
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to add gift to cart:', error);
    }
  }

  async adjustGiftQuantity(milestone, giftKey, cartItems, targetQuantity) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Adjusting free gift quantity:', giftKey, 'to', targetQuantity);
      
      // Find all FREE gift line items for this milestone (with gift properties)
      const freeGiftLineItems = cartItems.filter(item => 
        item.variant_id === parseInt(milestone.variantId) && 
        item.properties && 
        item.properties._gift === 'true' &&
        item.properties._gift_key === giftKey
      );

      if (freeGiftLineItems.length === 0) {
        this.debugLog('[GIFTS-PROGRESS] No free gift line items found for adjustment');
        return;
      }

      // If we have multiple free gift line items, remove all but the first one
      if (freeGiftLineItems.length > 1) {
        for (let i = 1; i < freeGiftLineItems.length; i++) {
          const lineNumber = cartItems.indexOf(freeGiftLineItems[i]) + 1;
          
          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0
            })
          });
          
          if (!response.ok) {
            this.debugWarn('[GIFTS-PROGRESS] Failed to remove duplicate free gift line item:', await response.text());
          }
        }
      }

      // Adjust the quantity of the first (or only) free gift line item
      const firstFreeGiftItem = freeGiftLineItems[0];
      const lineNumber = cartItems.indexOf(firstFreeGiftItem) + 1;
      
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: lineNumber,
          quantity: targetQuantity
        })
      });

      if (response.ok) {
        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        
        // Don't dispatch individual cart events - let the batch handler do it
        
        this.debugLog('[GIFTS-PROGRESS] Free gift quantity adjusted to', targetQuantity);
        this.debugLog('[GIFTS-PROGRESS] Note: Any additional quantities of this product will be paid items');
      } else {
        this.debugWarn('[GIFTS-PROGRESS] Failed to adjust free gift quantity:', await response.text());
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to adjust free gift quantity:', error);
    }
  }

  async removeGiftFromCart(milestone, giftKey, cartItems) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Removing free gift from cart:', giftKey);
      
      const giftData = this.addedGifts.get(giftKey);
      if (!giftData) {
        this.debugLog('[GIFTS-PROGRESS] Gift data not found for removal:', giftKey);
        return;
      }

      // Get fresh cart data to ensure we have the correct line numbers
      const freshCartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      const freshCart = await freshCartResponse.json();
      
      // Find all FREE gift line items for this milestone (with gift properties) in fresh cart
      const freeGiftLineItems = freshCart.items.filter(item => 
        item.variant_id === parseInt(milestone.variantId) && 
        item.properties && 
        item.properties._gift === 'true' &&
        item.properties._gift_key === giftKey
      );

      if (freeGiftLineItems.length === 0) {
        this.debugLog('[GIFTS-PROGRESS] No free gift line items found in fresh cart');
        // Still remove from tracking even if not found in cart
        this.addedGifts.delete(giftKey);
        this.giftLineItems.delete(giftKey);
        return;
      }

      this.debugLog('[GIFTS-PROGRESS] Found', freeGiftLineItems.length, 'free gift line items to remove in fresh cart');

      let removalSuccess = true;
      
      // Remove all free gift line items using fresh cart data
      for (const giftLineItem of freeGiftLineItems) {
        // Find the line number in the fresh cart
        const lineNumber = freshCart.items.indexOf(giftLineItem) + 1;
        
        this.debugLog('[GIFTS-PROGRESS] Removing line', lineNumber, 'for variant', giftLineItem.variant_id);
        
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          this.debugWarn('[GIFTS-PROGRESS] Failed to remove free gift line item:', errorText);
          removalSuccess = false;
        } else {
          this.debugLog('[GIFTS-PROGRESS] Successfully removed line', lineNumber);
        }
      }

      if (removalSuccess) {
        // Verify removal by fetching cart again
        const verifyCartResponse = await fetch(`${Shopify.routes.root}cart.js`);
        const verifyCart = await verifyCartResponse.json();
        
        // Check if gift is actually gone
        const remainingGifts = verifyCart.items.filter(item => 
          item.variant_id === parseInt(milestone.variantId) && 
          item.properties && 
          item.properties._gift === 'true' &&
          item.properties._gift_key === giftKey
        );
        
        if (remainingGifts.length === 0) {
          this.debugLog('[GIFTS-PROGRESS] ✅ Gift successfully removed from cart');
          // Remove from tracking
          this.addedGifts.delete(giftKey);
          this.giftLineItems.delete(giftKey);
          
          // Force a cart refresh to update the UI
          this.forceCartDrawerRefresh();
        } else {
          this.debugWarn('[GIFTS-PROGRESS] ⚠️ Gift still present after removal attempt:', remainingGifts.length, 'items remain');
        }
      }
      
      this.debugLog('[GIFTS-PROGRESS] Free gift removal process completed');
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to remove free gift from cart:', error);
    }
  }

  updateProgress() {
    this.debugLog('[GIFTS-PROGRESS] Updating progress, cart total:', this.cartTotal);
    
    const progressElement = this.querySelector('.gifts-progress__fill');
    const messageElement = this.querySelector('.gifts-progress__current-message');
    const mainMessageElement = this.querySelector('.gifts-progress__message');
    
    if (!progressElement || !messageElement) {
      this.debugLog('[GIFTS-PROGRESS] Missing required elements, skipping update');
      return;
    }

    // Filter out hidden milestones for visual display
    const visibleMilestones = this.milestones.filter(m => !m.hidden);
    const sortedMilestones = [...visibleMilestones].sort((a, b) => a.threshold - b.threshold);
    const maxThreshold = sortedMilestones[sortedMilestones.length - 1]?.threshold || 10000;
    
    // Update progress bar
    const progressPercentage = Math.min((this.cartTotal / maxThreshold) * 100, 100);
    progressElement.style.width = `${progressPercentage}%`;

    // Position milestones proportionally along the track
    sortedMilestones.forEach((milestone, index) => {
      const milestonePercentage = (milestone.threshold / maxThreshold) * 100;
      
      // Handle edge cases to prevent overflow
      let leftPosition = milestonePercentage;
      if (milestonePercentage >= 100) {
        leftPosition = 100;
        milestone.element.style.setProperty('transform', 'translateX(-100%)', 'important');
      } else if (milestonePercentage <= 0) {
        leftPosition = 0;
        milestone.element.style.setProperty('transform', 'translateX(0%)', 'important');
      } else {
        milestone.element.style.setProperty('transform', 'translateX(-50%)', 'important');
      }
      
      // Force the positioning with !important to override any CSS
      milestone.element.style.setProperty('left', `${leftPosition}%`, 'important');
      milestone.element.style.setProperty('position', 'absolute', 'important');
      
      const isAchieved = this.cartTotal >= milestone.threshold;
      milestone.element.classList.toggle('gifts-progress__milestone--achieved', isAchieved);
      
      this.debugLog(`[GIFTS-PROGRESS] Milestone ${milestone.threshold} (${index}): positioned at ${leftPosition}%, achieved: ${isAchieved}`);
    });

    // Find current status
    const achievedMilestones = sortedMilestones.filter(m => this.cartTotal >= m.threshold);
    const nextMilestone = sortedMilestones.find(m => this.cartTotal < m.threshold);

    // Update messages
    if (achievedMilestones.length > 0) {
      const latestAchieved = achievedMilestones[achievedMilestones.length - 1];
      if (latestAchieved.type === 'shipping') {
        mainMessageElement.innerHTML = 'You unlocked <strong>free shipping</strong>! 🎉';
      } else {
        mainMessageElement.innerHTML = '<strong>Free gift</strong> unlocked! 🎁';
      }
    } else {
      mainMessageElement.innerHTML = 'Now you can earn free gifts!';
    }

    if (nextMilestone) {
      const remaining = nextMilestone.threshold - this.cartTotal;
      const nextType = nextMilestone.type === 'shipping' ? 'free shipping' : 'a free gift';
      messageElement.innerHTML = `Add ${this.formatMoney(remaining)} more for <strong>${nextType}</strong>`;
    } else if (sortedMilestones.length > 0) {
      messageElement.innerHTML = '<strong>All rewards</strong> unlocked! 🎉';
    } else {
      messageElement.innerHTML = '';
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: Shopify.currency?.active || 'USD'
    }).format(cents / 100);
  }

  dispatchCartChangeEvent(cart, baseEvent) {
    try {
      // Clear any existing debounce timer
      if (this.cartEventDebounceTimer) {
        clearTimeout(this.cartEventDebounceTimer);
      }
      
      // Debounce cart events to prevent rapid-fire refreshes
      this.cartEventDebounceTimer = setTimeout(() => {
        // Only dispatch if cart total actually changed
        if (cart.total_price !== this.lastCartTotal) {
          this.lastCartTotal = cart.total_price;
          
          // Dispatch the theme's cart:change event
          document.dispatchEvent(new CustomEvent('cart:change', {
            detail: { 
              cart: cart,
              baseEvent: baseEvent
            }
          }));
          
          // Only dispatch cart:refresh if we made significant changes
          if (this.pendingCartUpdates.size > 0) {
            this.forceCartDrawerRefresh();
          }
          
          this.debugLog('[GIFTS-PROGRESS] Dispatched debounced cart change event');
        } else {
          this.debugLog('[GIFTS-PROGRESS] Skipped cart event (no total change)');
        }
      }, 100); // 100ms debounce
      
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to dispatch cart change event:', error);
    }
  }
}

// Register the custom element
window.customElements.define('gifts-progress', GiftsProgress);

} // End of redeclaration check